import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { sendMessageWithConvId } from '../../features/messenger/conversationSlice'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import SpinnerSmall from 'components/SpinnerSmall'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'

function NewMessageForm({ id, recipientPublicKey }) {
  const { t } = useTranslation('messaging')
  const dispatch = useDispatch()
  const [messageText, setMessageText] = useState('')
  const [image, setImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const { sendMessageStatus } = useSelector((state) => state.conversations)
  const sending = sendMessageStatus === 'LOADING'

  const removeImage = () => {
    setImage(null)
    setImagePreview(null)
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!messageText.trim() && !image) {
      toast.error(t('errors.emptyMessage'))
      return
    }

    const formData = new FormData()
    if (image) formData.append('file', image)
    formData.append('text', messageText)
    formData.append('conversationId', id)
    if (recipientPublicKey) formData.append('recipientPublicKey', recipientPublicKey)
    dispatch(sendMessageWithConvId(formData))
    setMessageText('')
    removeImage()
  }

  const handleAddImage = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (event) => {
      const file = event.target.files?.[0]
      if (!file?.type.startsWith('image/')) {
        toast.error(t('attachments.invalidImage'))
        return
      }
      setImage(file)
      const reader = new FileReader()
      reader.onload = () => setImagePreview(reader.result)
      reader.readAsDataURL(file)
    }
    input.click()
  }

  return (
    <ComposerContainer>
      {imagePreview && (
        <ImagePreview>
          <img src={imagePreview} alt={t('attachments.preview')} />
          <Tooltip title={t('attachments.removeImage')} placement='top'>
            <RemoveImage type='button' onClick={removeImage} aria-label={t('attachments.removeImage')}>
              <CloseRoundedIcon />
            </RemoveImage>
          </Tooltip>
        </ImagePreview>
      )}
      <Composer onSubmit={handleSubmit}>
        <Tooltip title={t('attachments.addImage')} placement='top'>
          <AttachmentButton type='button' onClick={handleAddImage} aria-label={t('attachments.addImage')}>
            <AddPhotoAlternateRoundedIcon />
          </AttachmentButton>
        </Tooltip>
        <textarea
          value={messageText}
          onChange={(event) => setMessageText(event.target.value)}
          placeholder={t('conversation.messagePlaceholder')}
          aria-label={t('conversation.messagePlaceholder')}
          maxLength={5000}
        />
        <SendButton
          type='submit'
          disabled={sending || (!messageText.trim() && !image)}
          aria-label={t('conversation.send')}
        >
          {sending ? <SpinnerSmall /> : <SendRoundedIcon />}
        </SendButton>
      </Composer>
    </ComposerContainer>
  )
}

export default NewMessageForm

const ComposerContainer = styled.div`
  flex: 0 0 auto;
  padding: 12px 14px;
  border-top: 1px solid #e8ecee;
  background: white;
`

const Composer = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 9px;

  textarea {
    flex: 1;
    min-width: 0;
    min-height: 42px;
    max-height: 120px;
    resize: vertical;
    padding: 11px 14px;
    border: 1px solid #dfe5e7;
    border-radius: 20px;
    outline: none;
    background: #f1f4f5;
    color: #293539;
    font-family: inherit;
    font-size: 13px;

    &:focus {
      border-color: var(--themeColor);
      box-shadow: 0 0 0 3px rgba(0, 180, 204, 0.1);
    }
  }
`

const ComposerButton = styled.button`
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`

const AttachmentButton = styled(ComposerButton)`
  background: #edf1f2;
  color: #526064;

  &:hover {
    background: #e2e8ea;
  }
`

const SendButton = styled(ComposerButton)`
  background: var(--themeColor);
  color: white;
`

const ImagePreview = styled.div`
  position: relative;
  width: fit-content;
  margin: 0 0 10px 51px;

  img {
    display: block;
    width: 110px;
    height: 90px;
    border-radius: 12px;
    object-fit: cover;
    box-shadow: 0 1px 5px rgba(20, 35, 40, 0.18);
  }
`

const RemoveImage = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 27px;
  height: 27px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #526064;
  color: white;
  cursor: pointer;

  svg {
    font-size: 18px;
  }
`
